'use client'

import { BackdropLoading } from '@/components/backdrop-loading'
import {
    registerPF,
    registerPJ,
    resolveRegistrationLink,
} from '@/lib/api/auth/auth'
import { AuthResponse } from '@/lib/api/common/interfaces/auth-response.interface'
import {
    CompanySecondarySegment,
    CompanySegment,
    Plan,
} from '@/lib/api/common/interfaces/company.interface'
import { ApiResponse } from '@/lib/api/request'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdArrowForward,
    MdBadge,
    MdBusiness,
    MdCheckCircle,
    MdEmail,
    MdInfoOutline,
    MdMarkEmailRead,
    MdPerson,
    MdPhone,
    MdShield,
    MdStorefront,
} from 'react-icons/md'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

/**
 * Rótulos das features do plano em português.
 *
 * As chaves são fixas — vêm de `buildFeaturesObject`, na tela master de planos —
 * e os valores numéricos aceitam a string `'unlimited'`. Antes a lista era
 * gerada por regex sobre o camelCase da chave, então quem ia contratar lia
 * "Monthly Attendances: 200".
 */
const FEATURE_LABELS: Record<
    string,
    { singular: string; plural: string; unlimited: string }
> = {
    aiCredits: {
        singular: 'crédito de IA por mês',
        plural: 'créditos de IA por mês',
        unlimited: 'Créditos de IA ilimitados',
    },
    users: {
        singular: 'usuário',
        plural: 'usuários',
        unlimited: 'Usuários ilimitados',
    },
    aiUsers: {
        singular: 'usuário de IA',
        plural: 'usuários de IA',
        unlimited: 'Usuários de IA ilimitados',
    },
    departments: {
        singular: 'setor de atendimento',
        plural: 'setores de atendimento',
        unlimited: 'Setores de atendimento ilimitados',
    },
    teleconsultationMinutes: {
        singular: 'minuto de teleconsulta por mês',
        plural: 'minutos de teleconsulta por mês',
        unlimited: 'Teleconsulta sem limite de minutos',
    },
    monthlySignDocuments: {
        singular: 'documento assinado por mês',
        plural: 'documentos assinados por mês',
        unlimited: 'Assinatura de documentos ilimitada',
    },
    monthlyFiscalNotes: {
        singular: 'nota fiscal por mês',
        plural: 'notas fiscais por mês',
        unlimited: 'Notas fiscais ilimitadas',
    },
    stores: {
        singular: 'loja virtual',
        plural: 'lojas virtuais',
        unlimited: 'Lojas virtuais ilimitadas',
    },
}

/** Features booleanas: só entram na lista quando vêm ligadas. */
const FEATURE_FLAGS: Record<string, string> = {
    support: 'Suporte incluído',
    resale: 'Revenda habilitada',
    customizations: 'Personalizações sob demanda',
}

/**
 * Traduz o objeto de features na lista do "Incluído". Zero e `false` ficam de
 * fora — a lista mostra o que o plano dá, não o que ele nega. Chave sem
 * mapeamento é ignorada, em vez de aparecer crua para o cliente.
 */
function buildFeatureList(features: Record<string, unknown>): string[] {
    const list: string[] = []

    for (const [key, label] of Object.entries(FEATURE_LABELS)) {
        const value = features[key]
        if (value === 'unlimited') {
            list.push(label.unlimited)
            continue
        }
        const quantity =
            typeof value === 'number'
                ? value
                : parseInt(String(value ?? ''), 10)
        if (!Number.isFinite(quantity) || quantity <= 0) continue
        list.push(
            `${quantity} ${quantity === 1 ? label.singular : label.plural}`
        )
    }

    for (const [key, label] of Object.entries(FEATURE_FLAGS)) {
        if (features[key] === true) list.push(label)
    }

    return list
}

export default function AuthRegisterPage() {
    const [isLoading, setIsLoading] = useState(false)
    const [loadingPlan, setLoadingPlan] = useState(true)
    // Período grátis concedido pelo LINK usado (0 = nenhum). Não vem do plano:
    // o mesmo plano pode ser negociado com cortesias diferentes.
    const [trialDays, setTrialDays] = useState(0)
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [cpf, setCpf] = useState('')
    const [cnpj, setCnpj] = useState('')
    const [companyName, setCompanyName] = useState('')
    const [tradingName, setTradingName] = useState('')
    const [plan, setPlan] = useState<Plan | null>(null)
    const [currentStep, setCurrentStep] = useState(1)
    const params = useSearchParams()
    const [showEmailSentMessage, setShowEmailSentMessage] = useState(false)
    const [segment, setSegment] = useState<CompanySegment>(
        CompanySegment.HEALTH
    )
    const [secondarySegment, setSecondarySegment] =
        useState<CompanySecondarySegment>(CompanySecondarySegment.OTHER)

    const { isAuthenticated } = useAuth()
    const navigate = useNavigate()

    // O painel master gera DOIS links distintos do mesmo token (um PF, um PJ), então
    // um link real sempre traz `type` explícito. Quando ele vem declarado, a natureza
    // fica travada: trocar aqui contornaria a escolha de quem enviou o link — e como
    // PF e PJ têm regras diferentes (documento, filial, nota fiscal), a conta nasceria
    // com uma natureza que o remetente não autorizou.
    const requestedType = params[0].get('type')
    const type = requestedType ?? 'pf'
    const isTypeLocked = requestedType === 'pf' || requestedType === 'pj'
    const token = params[0].get('token')

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/')
            return
        }

        if (!token) {
            toast.error('Link de cadastro inválido')
            navigate('/login')
            return
        }

        loadPlan()
    }, [isAuthenticated, token, navigate])

    const loadPlan = async () => {
        if (!token) return

        try {
            setLoadingPlan(true)
            const response = await resolveRegistrationLink(token)

            if (response.success && response.result?.plan) {
                setPlan(response.result.plan as Plan)
                setTrialDays(response.result.trialDays ?? 0)
            } else {
                toast.error('Link de cadastro inválido ou já utilizado')
                navigate('/login')
            }
        } catch (error: any) {
            console.error('Erro ao carregar plano:', error)
            toast.error('Link de cadastro inválido ou já utilizado')
            navigate('/login')
        } finally {
            setLoadingPlan(false)
        }
    }

    const totalSteps = 2

    const canGoToNextStep = () => {
        if (currentStep === 1) {
            return name.trim() && email.trim()
        }
        if (currentStep === 2) {
            if (type === 'pf') {
                // Segmento secundário não é perguntado em PF (assume atendimento
                // direto), então não entra na validação do passo.
                return cpf.trim() && phone.trim() && segment
            } else {
                return (
                    cnpj.trim() &&
                    companyName.trim() &&
                    tradingName.trim() &&
                    phone.trim() &&
                    segment &&
                    secondarySegment
                )
            }
        }
        return false
    }

    const handleNext = () => {
        if (!canGoToNextStep()) return
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1)
        }
    }

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1)
        }
    }

    const formatCpfInput = (value: string) => {
        const numbers = value.replace(/\D/g, '').slice(0, 11)
        if (numbers.length <= 3) return numbers
        if (numbers.length <= 6)
            return `${numbers.slice(0, 3)}.${numbers.slice(3)}`
        if (numbers.length <= 9)
            return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`
        return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9)}`
    }

    const formatCnpjInput = (value: string) => {
        const numbers = value.replace(/\D/g, '').slice(0, 14)
        if (numbers.length <= 2) return numbers
        if (numbers.length <= 5)
            return `${numbers.slice(0, 2)}.${numbers.slice(2)}`
        if (numbers.length <= 8)
            return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`
        if (numbers.length <= 12)
            return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`
        return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12)}`
    }

    const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCpf(formatCpfInput(e.target.value))
    }

    const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCnpj(formatCnpjInput(e.target.value))
    }

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    const validateCPF = (cpf: string) => {
        const cleanCpf = cpf.replace(/[^\d]/g, '')
        if (cleanCpf.length !== 11) return false
        if (/^(\d)\1{10}$/.test(cleanCpf)) return false

        let sum = 0
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cleanCpf.charAt(i)) * (10 - i)
        }
        let digit = 11 - (sum % 11)
        if (digit >= 10) digit = 0
        if (digit !== parseInt(cleanCpf.charAt(9))) return false

        sum = 0
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cleanCpf.charAt(i)) * (11 - i)
        }
        digit = 11 - (sum % 11)
        if (digit >= 10) digit = 0
        if (digit !== parseInt(cleanCpf.charAt(10))) return false

        return true
    }

    const validateCNPJ = (cnpj: string) => {
        const cleanCnpj = cnpj.replace(/[^\d]/g, '')
        if (cleanCnpj.length !== 14) return false
        if (/^(\d)\1{13}$/.test(cleanCnpj)) return false

        let length = cleanCnpj.length - 2
        let numbers = cleanCnpj.substring(0, length)
        const digits = cleanCnpj.substring(length)
        let sum = 0
        let pos = length - 7

        for (let i = length; i >= 1; i--) {
            sum += parseInt(numbers.charAt(length - i)) * pos--
            if (pos < 2) pos = 9
        }

        let result = sum % 11 < 2 ? 0 : 11 - (sum % 11)
        if (result !== parseInt(digits.charAt(0))) return false

        length = length + 1
        numbers = cleanCnpj.substring(0, length)
        sum = 0
        pos = length - 7

        for (let i = length; i >= 1; i--) {
            sum += parseInt(numbers.charAt(length - i)) * pos--
            if (pos < 2) pos = 9
        }

        result = sum % 11 < 2 ? 0 : 11 - (sum % 11)
        if (result !== parseInt(digits.charAt(1))) return false

        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)

        if (!token) {
            toast.error('Link de cadastro inválido. Redirecionando...')
            setTimeout(() => navigate('/login'), 2000)
            setIsLoading(false)
            return
        }

        if (!name.trim()) {
            toast.warning('Por favor, preencha o nome completo.')
            setIsLoading(false)
            return
        }

        if (!validateEmail(email)) {
            toast.warning('Por favor, insira um e-mail válido.')
            setIsLoading(false)
            return
        }

        if (type === 'pf') {
            if (!cpf.trim()) {
                toast.warning('Por favor, preencha o CPF.')
                setIsLoading(false)
                return
            }
            if (!validateCPF(cpf)) {
                toast.warning('Por favor, insira um CPF válido.')
                setIsLoading(false)
                return
            }
        } else {
            if (!cnpj.trim()) {
                toast.warning('Por favor, preencha o CNPJ.')
                setIsLoading(false)
                return
            }
            if (!validateCNPJ(cnpj)) {
                toast.warning('Por favor, insira um CNPJ válido.')
                setIsLoading(false)
                return
            }
        }

        if (!phone.trim()) {
            toast.warning('Por favor, preencha o telefone da empresa.')
            setIsLoading(false)
            return
        }

        let response: ApiResponse<AuthResponse> | null = null

        const normalizedPhone = normalizePhoneNumber(phone)

        // Sem senha: a conta é criada sem senha e o usuário recebe um link por
        // email para confirmar o email e criar a própria senha (aceitando os
        // termos nesse momento).
        if (type === 'pf') {
            response = await registerPF({
                email,
                name,
                phone: normalizedPhone,
                cpf,
                registrationToken: token,
                segment,
                secondarySegment,
            })
        } else {
            response = await registerPJ({
                email,
                name,
                phone: normalizedPhone,
                cnpj,
                companyName,
                tradingName,
                registrationToken: token,
                segment,
                secondarySegment,
            })
        }

        if (response.success) {
            setShowEmailSentMessage(true)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
            toast.error(
                response.error ||
                    'Ocorreu um erro ao criar a conta. Tente novamente.'
            )
        }

        setIsLoading(false)
    }

    const isFormValid = Boolean(canGoToNextStep())

    const formatPhoneNumber = (value: string) => {
        const numbers = value.replace(/\D/g, '')

        if (numbers.length === 0) return ''

        if (numbers.length <= 2) {
            return `(${numbers}`
        }

        if (numbers.length <= 6) {
            return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`
        }

        if (numbers.length <= 10) {
            return `(${numbers.slice(0, 2)}) ${numbers.slice(
                2,
                6
            )}-${numbers.slice(6)}`
        }

        if (numbers.length === 11) {
            return `(${numbers.slice(0, 2)}) ${numbers.slice(
                2,
                7
            )}-${numbers.slice(7)}`
        }

        if (numbers.length > 11) {
            return `(${numbers.slice(0, 2)}) ${numbers.slice(
                2,
                7
            )}-${numbers.slice(7, 11)}`
        }

        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(
            7
        )}`
    }

    const normalizePhoneNumber = (value: string): string => {
        return value.replace(/\D/g, '')
    }

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatPhoneNumber(e.target.value)
        setPhone(formatted)
    }

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(price / 100)
    }

    const planFeatureList = plan?.features
        ? buildFeatureList(plan.features as Record<string, unknown>)
        : []

    /** Iniciais do resumo. Sobrenome quando existe, senão as duas primeiras letras. */
    const initials = (() => {
        const parts = name.trim().split(/\s+/).filter(Boolean)
        if (parts.length === 0) return ''
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    })()

    const documentLabel = type === 'pf' ? cpf : cnpj

    const steps = [
        { number: 1, label: 'Você', hint: 'Nome e e-mail' },
        {
            number: 2,
            label: type === 'pf' ? 'Documentos' : 'Empresa',
            hint:
                type === 'pf'
                    ? 'CPF, telefone e atuação'
                    : 'CNPJ, razão social e telefone',
        },
    ]

    if (loadingPlan) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-base-200/40">
                <div className="flex flex-col items-center gap-4">
                    <span className="loading loading-spinner loading-lg text-secondary"></span>
                    <p className="text-sm text-base-content/50">
                        Carregando informações do plano...
                    </p>
                </div>
            </div>
        )
    }

    if (showEmailSentMessage) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-base-200/40 p-4">
                <div className="w-full max-w-md animate-fade-in-up">
                    <div className="overflow-hidden rounded-3xl border border-base-300 bg-base-100 shadow-xl">
                        <div className="brand-surface px-8 py-10 text-center text-primary-content">
                            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
                                <MdMarkEmailRead className="text-4xl" />
                            </div>
                            <h1 className="text-2xl font-bold tracking-tight">
                                Conta criada
                            </h1>
                            <p className="mt-1.5 text-primary-content/80">
                                Falta só criar sua senha
                            </p>
                        </div>

                        <div className="flex flex-col gap-5 p-8">
                            {/* O e-mail aparece aqui de propósito: é onde a pessoa
                                descobre que digitou errado. Antes a tela só dizia
                                "enviamos um link" e ela não tinha onde conferir. */}
                            <div className="rounded-2xl border border-base-300 bg-base-200/50 p-4 text-center">
                                <p className="text-xs uppercase tracking-wide text-base-content/40">
                                    Enviamos o link para
                                </p>
                                <p className="mt-1 break-all font-semibold text-base-content">
                                    {email}
                                </p>
                            </div>

                            <ol className="flex flex-col gap-3">
                                {[
                                    'Abra sua caixa de entrada',
                                    'Clique no link que enviamos',
                                    'Crie sua senha e entre na conta',
                                ].map((passo, indice) => (
                                    <li
                                        key={indice}
                                        className="flex items-start gap-3 text-sm leading-relaxed text-base-content/70"
                                    >
                                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary/15 text-xs font-semibold text-secondary">
                                            {indice + 1}
                                        </span>
                                        {passo}
                                    </li>
                                ))}
                            </ol>

                            <p className="text-xs leading-relaxed text-base-content/40">
                                Não chegou em alguns minutos? Confira a pasta de
                                spam ou lixo eletrônico.
                            </p>

                            <Link to="/login" className="btn btn-secondary">
                                Ir para o login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-base-200/40 px-4 py-10 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-5xl">
                <div className="mb-8 flex items-center gap-2.5">
                    <img
                        src="/logo512.png"
                        alt="MediarDex"
                        className="h-9 w-9 object-contain"
                    />
                    <span className="text-lg font-bold tracking-tight text-secondary">
                        MediarDex
                    </span>
                </div>

                <div className="grid items-start gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
                    {/* Coluna de contexto: o que está sendo criado. */}
                    <div className="flex flex-col gap-4 lg:sticky lg:top-10">
                        {/* Resumo que se preenche enquanto a pessoa digita.
                            Deliberadamente NÃO parece um cartão: retângulo com
                            gradiente, iniciais, documento em fonte mono e dois
                            campos lado a lado no rodapé desenham um cartão
                            bancário, e numa tela de cadastro isso faz a pessoa
                            achar que vai cadastrar ou receber um. Aqui é uma
                            ficha: linhas verticais com rótulo, valor e um check
                            por campo preenchido. */}
                        <div className="rounded-3xl border border-base-300 bg-base-100 p-6">
                            <div className="flex items-center justify-between gap-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-base-content/40">
                                    Resumo do cadastro
                                </p>
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary/10 px-2.5 py-1 text-xs font-semibold text-secondary">
                                    {type === 'pf' ? (
                                        <MdPerson className="text-sm" />
                                    ) : (
                                        <MdBusiness className="text-sm" />
                                    )}
                                    {type === 'pf'
                                        ? 'Pessoa física'
                                        : 'Pessoa jurídica'}
                                </span>
                            </div>

                            <div className="mt-5 flex items-center gap-3">
                                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-secondary/10 text-base font-bold text-secondary">
                                    {initials || (
                                        <MdPerson className="text-xl text-base-content/30" />
                                    )}
                                </span>
                                <div className="min-w-0">
                                    <p
                                        className={`truncate font-semibold ${name ? 'text-base-content' : 'text-base-content/30'}`}
                                    >
                                        {name || 'Seu nome'}
                                    </p>
                                    <p
                                        className={`truncate text-sm ${email ? 'text-base-content/60' : 'text-base-content/30'}`}
                                    >
                                        {email || 'seu@email.com.br'}
                                    </p>
                                </div>
                            </div>

                            <dl className="mt-5 flex flex-col gap-3 border-t border-base-300 pt-5">
                                {[
                                    ...(type === 'pj'
                                        ? [
                                              {
                                                  label: 'Empresa',
                                                  value:
                                                      tradingName ||
                                                      companyName,
                                              },
                                          ]
                                        : []),
                                    {
                                        label: type === 'pf' ? 'CPF' : 'CNPJ',
                                        value: documentLabel,
                                    },
                                    { label: 'Telefone', value: phone },
                                ].map(item => (
                                    <div
                                        key={item.label}
                                        className="flex items-center justify-between gap-3"
                                    >
                                        <dt className="shrink-0 text-sm text-base-content/50">
                                            {item.label}
                                        </dt>
                                        <dd className="flex min-w-0 items-center gap-1.5">
                                            <span
                                                className={`truncate text-sm ${item.value ? 'font-medium text-base-content' : 'text-base-content/30'}`}
                                            >
                                                {item.value || 'A preencher'}
                                            </span>
                                            {item.value && (
                                                <MdCheckCircle className="shrink-0 text-base text-success" />
                                            )}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>

                        {/* Plano: o que a pessoa está contratando, com os recursos
                            à vista. Antes vinham fechados num accordion e, ao
                            abrir, mostravam a chave crua do backend. */}
                        {plan && (
                            <div className="rounded-3xl border border-base-300 bg-base-100 p-6">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-xs uppercase tracking-wide text-base-content/40">
                                            Plano
                                        </p>
                                        <h2 className="truncate text-lg font-bold text-base-content">
                                            {plan.name}
                                        </h2>
                                    </div>
                                    <div className="shrink-0 text-right">
                                        <p className="text-xl font-bold text-secondary">
                                            {plan.price === 0
                                                ? 'Grátis'
                                                : formatPrice(plan.price)}
                                        </p>
                                        {plan.price > 0 && (
                                            <p className="text-xs text-base-content/40">
                                                {trialDays > 0
                                                    ? 'por mês, após o período grátis'
                                                    : 'por mês'}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* A cortesia é o principal argumento da tela e
                                    precisa aparecer antes dos recursos. */}
                                {trialDays > 0 && (
                                    <div className="mt-4 flex items-start gap-2 rounded-2xl bg-secondary/10 p-3">
                                        <MdCheckCircle className="mt-0.5 shrink-0 text-base text-secondary" />
                                        <p className="text-sm text-base-content/80">
                                            <strong>
                                                {trialDays}{' '}
                                                {trialDays === 1
                                                    ? 'dia grátis'
                                                    : 'dias grátis'}
                                            </strong>{' '}
                                            para testar. A primeira cobrança só
                                            acontece depois desse período.
                                        </p>
                                    </div>
                                )}

                                {plan.description && (
                                    <p className="mt-3 text-sm leading-relaxed text-base-content/60">
                                        {plan.description}
                                    </p>
                                )}

                                {planFeatureList.length > 0 && (
                                    <div className="mt-5 border-t border-base-300 pt-5">
                                        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-base-content/40">
                                            Incluído
                                        </p>
                                        <ul className="flex flex-col gap-2">
                                            {planFeatureList.map(feature => (
                                                <li
                                                    key={feature}
                                                    className="flex items-start gap-2 text-sm text-base-content/70"
                                                >
                                                    <MdCheckCircle className="mt-0.5 shrink-0 text-base text-secondary" />
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Dito explicitamente: cadastro é o momento em que a
                            pessoa teme ser cobrada, e o silêncio sobre isso é o
                            que gera a dúvida. Nenhum dado de pagamento é pedido
                            nesta tela. */}
                        <div className="flex items-start gap-2.5 rounded-2xl border border-base-300 bg-base-100 p-4">
                            <MdShield className="mt-0.5 shrink-0 text-lg text-base-content/40" />
                            <p className="text-xs leading-relaxed text-base-content/50">
                                Conexão segura. Não pedimos dados de pagamento
                                neste cadastro.
                            </p>
                        </div>
                    </div>

                    {/* Formulário */}
                    <div className="rounded-3xl border border-base-300 bg-base-100 p-6 sm:p-8">
                        <div className="mb-7">
                            <h1 className="text-2xl font-bold tracking-tight text-base-content sm:text-3xl">
                                Criar conta
                            </h1>
                            <p className="mt-1 text-base-content/50">
                                Passo {currentStep} de {totalSteps} ·{' '}
                                {steps[currentStep - 1].hint}
                            </p>
                        </div>

                        {/* Passos com rótulo: duas barrinhas anônimas não diziam
                            quantas etapas eram nem o que vinha depois. */}
                        <div className="mb-7 flex gap-3">
                            {steps.map(step => {
                                const done = step.number < currentStep
                                const current = step.number === currentStep
                                return (
                                    <div
                                        key={step.number}
                                        className="flex flex-1 flex-col gap-2"
                                    >
                                        <span
                                            className={`h-1 rounded-full transition-colors ${
                                                done || current
                                                    ? 'bg-secondary'
                                                    : 'bg-base-300'
                                            }`}
                                        />
                                        <span className="flex items-center gap-1.5 text-xs">
                                            {done ? (
                                                <MdCheckCircle className="text-sm text-secondary" />
                                            ) : (
                                                <span
                                                    className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-semibold ${
                                                        current
                                                            ? 'bg-secondary text-secondary-content'
                                                            : 'bg-base-300 text-base-content/50'
                                                    }`}
                                                >
                                                    {step.number}
                                                </span>
                                            )}
                                            <span
                                                className={
                                                    current
                                                        ? 'font-semibold text-base-content'
                                                        : 'text-base-content/50'
                                                }
                                            >
                                                {step.label}
                                            </span>
                                        </span>
                                    </div>
                                )
                            })}
                        </div>

                        {/*
                            Link com `type` declarado (todo link gerado no painel
                            master) trava a natureza: o cartão ao lado mostra qual
                            conta está sendo criada, sem permitir trocar. Só um
                            link sem `type` — cenário legado — cai no seletor.
                        */}
                        {!isTypeLocked && (
                            <div className="mb-6 grid gap-3 sm:grid-cols-2">
                                {[
                                    {
                                        kind: 'pf',
                                        icon: <MdPerson className="text-xl" />,
                                        title: 'Pessoa física',
                                        hint: 'Atendo com meu CPF',
                                    },
                                    {
                                        kind: 'pj',
                                        icon: (
                                            <MdBusiness className="text-xl" />
                                        ),
                                        title: 'Pessoa jurídica',
                                        hint: 'Tenho CNPJ',
                                    },
                                ].map(option => {
                                    const selected = type === option.kind
                                    return (
                                        <button
                                            key={option.kind}
                                            type="button"
                                            onClick={() =>
                                                navigate(
                                                    `/register?type=${option.kind}&token=${token}`
                                                )
                                            }
                                            className={`relative flex items-center gap-3 rounded-xl border p-4 text-left transition-colors ${
                                                selected
                                                    ? 'border-secondary bg-secondary/5 ring-1 ring-secondary/30'
                                                    : 'border-base-300 hover:border-base-content/20 hover:bg-base-200/50'
                                            }`}
                                        >
                                            <span
                                                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                                                    selected
                                                        ? 'bg-secondary text-secondary-content'
                                                        : 'bg-base-200 text-base-content/40'
                                                }`}
                                            >
                                                {option.icon}
                                            </span>
                                            <span className="min-w-0">
                                                <span className="block text-sm font-semibold text-base-content">
                                                    {option.title}
                                                </span>
                                                <span className="block truncate text-xs text-base-content/50">
                                                    {option.hint}
                                                </span>
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>
                        )}

                        <form
                            className="flex flex-col gap-5"
                            onSubmit={handleSubmit}
                        >
                            {/* Passo 1: Nome e Email */}
                            {currentStep === 1 && (
                                <div className="flex animate-fade-in flex-col gap-5">
                                    <RegisterField
                                        icon={<MdPerson />}
                                        label="Nome completo do responsável"
                                        name="name"
                                        type="text"
                                        placeholder="Como você se chama"
                                        autoComplete="name"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        required
                                    />
                                    <RegisterField
                                        icon={<MdEmail />}
                                        label="E-mail"
                                        name="email"
                                        type="email"
                                        placeholder="voce@empresa.com.br"
                                        autoComplete="email"
                                        inputMode="email"
                                        hint="É para este endereço que vai o link de criação de senha."
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        required
                                    />
                                </div>
                            )}

                            {/* Passo 2: CPF/CNPJ, Telefone e Segmento */}
                            {currentStep === 2 && (
                                <div className="flex animate-fade-in flex-col gap-5">
                                    {type === 'pf' ? (
                                        <>
                                            <RegisterField
                                                icon={<MdBadge />}
                                                label="CPF"
                                                name="cpf"
                                                type="text"
                                                placeholder="000.000.000-00"
                                                inputMode="numeric"
                                                value={cpf}
                                                onChange={handleCpfChange}
                                                required
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <RegisterField
                                                icon={<MdBadge />}
                                                label="CNPJ"
                                                name="cnpj"
                                                type="text"
                                                placeholder="00.000.000/0000-00"
                                                inputMode="numeric"
                                                value={cnpj}
                                                onChange={handleCnpjChange}
                                                required
                                            />
                                            <RegisterField
                                                icon={<MdBusiness />}
                                                label="Razão social"
                                                name="companyName"
                                                type="text"
                                                placeholder="Como consta no CNPJ"
                                                autoComplete="organization"
                                                value={companyName}
                                                onChange={e =>
                                                    setCompanyName(
                                                        e.target.value
                                                    )
                                                }
                                                required
                                            />
                                            <RegisterField
                                                icon={<MdStorefront />}
                                                label="Nome fantasia"
                                                name="tradingName"
                                                type="text"
                                                placeholder="Como o cliente conhece a empresa"
                                                value={tradingName}
                                                onChange={e =>
                                                    setTradingName(
                                                        e.target.value
                                                    )
                                                }
                                                required
                                            />
                                        </>
                                    )}
                                    <RegisterField
                                        icon={<MdPhone />}
                                        label={
                                            type === 'pf'
                                                ? 'Telefone'
                                                : 'Telefone da empresa'
                                        }
                                        name="phone"
                                        type="text"
                                        placeholder="(00) 00000-0000"
                                        inputMode="tel"
                                        autoComplete="tel"
                                        value={phone}
                                        onChange={handlePhoneChange}
                                        required
                                    />

                                    <div className="flex flex-col gap-2">
                                        <label
                                            htmlFor="register-segment"
                                            className="flex items-center gap-1.5 text-sm font-medium text-base-content"
                                        >
                                            {type === 'pf'
                                                ? 'Área de atuação'
                                                : 'Segmento'}
                                            <span className="text-error">
                                                *
                                            </span>
                                        </label>
                                        <select
                                            id="register-segment"
                                            name="segment"
                                            value={segment}
                                            onChange={e =>
                                                setSegment(
                                                    e.target
                                                        .value as CompanySegment
                                                )
                                            }
                                            className="select select-bordered w-full"
                                            required
                                        >
                                            <option
                                                value={CompanySegment.HEALTH}
                                            >
                                                Saúde
                                            </option>
                                            <option
                                                value={CompanySegment.BEAUTY}
                                            >
                                                Beleza e Estética
                                            </option>
                                            <option
                                                value={CompanySegment.FITNESS}
                                            >
                                                Academia e Fitness
                                            </option>
                                            <option
                                                value={CompanySegment.PET_SHOP}
                                            >
                                                Pet Shop e Veterinária
                                            </option>
                                            <option
                                                value={
                                                    CompanySegment.DIRECT_SALE
                                                }
                                            >
                                                Venda com preços fixos
                                            </option>
                                            <option
                                                value={
                                                    CompanySegment.QUOTE_BASED_SALE
                                                }
                                            >
                                                Venda com orçamento
                                            </option>
                                            <option
                                                value={CompanySegment.OTHER}
                                            >
                                                Outro
                                            </option>
                                        </select>
                                        <p className="text-xs leading-relaxed text-base-content/50">
                                            Define quais telas aparecem no seu
                                            menu. Pode ser alterado depois.
                                        </p>
                                    </div>

                                    {/*
                                        Segmento secundário não é perguntado no
                                        cadastro de pessoa física: "Intermediação"
                                        ESCONDE prescrições, triagem, atestados e
                                        teleconsultas do menu, e quem se cadastra
                                        como PF é o profissional que atende — a
                                        resposta errada aqui tiraria dele justamente
                                        as telas que vai usar. Assume atendimento
                                        direto (o default do estado) e segue
                                        editável na aba Empresa.
                                    */}
                                    {type !== 'pf' && (
                                        <div className="flex flex-col gap-2">
                                            <label
                                                htmlFor="register-secondary-segment"
                                                className="flex items-center gap-1.5 text-sm font-medium text-base-content"
                                            >
                                                Como a empresa atende
                                                <span className="text-error">
                                                    *
                                                </span>
                                            </label>
                                            <select
                                                id="register-secondary-segment"
                                                name="secondarySegment"
                                                value={secondarySegment}
                                                onChange={e =>
                                                    setSecondarySegment(
                                                        e.target
                                                            .value as CompanySecondarySegment
                                                    )
                                                }
                                                className="select select-bordered w-full"
                                                required
                                            >
                                                <option
                                                    value={
                                                        CompanySecondarySegment.OTHER
                                                    }
                                                >
                                                    {`Atendimento direto — eu atendo o ${
                                                        segment ===
                                                        CompanySegment.HEALTH
                                                            ? 'paciente'
                                                            : 'cliente'
                                                    }`}
                                                </option>
                                                <option
                                                    value={
                                                        CompanySecondarySegment.INTERMEDIATION
                                                    }
                                                >
                                                    Intermediação — encaminho
                                                    para quem atende
                                                </option>
                                            </select>
                                            {segment ===
                                                CompanySegment.HEALTH && (
                                                <p className="text-xs leading-relaxed text-base-content/50">
                                                    Em intermediação,
                                                    prescrições, triagem,
                                                    atestados e teleconsulta
                                                    não aparecem no menu.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="flex items-center justify-between gap-3 border-t border-base-300 pt-5">
                                {currentStep > 1 ? (
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className="btn btn-ghost gap-2"
                                    >
                                        <MdArrowBack />
                                        Voltar
                                    </button>
                                ) : (
                                    <span className="text-sm text-base-content/50">
                                        Já tem uma conta?{' '}
                                        <Link
                                            to="/login"
                                            className="font-semibold text-secondary hover:underline"
                                        >
                                            Entrar
                                        </Link>
                                    </span>
                                )}

                                {currentStep < totalSteps ? (
                                    <button
                                        type="button"
                                        onClick={handleNext}
                                        disabled={!canGoToNextStep()}
                                        className="btn btn-secondary gap-2"
                                    >
                                        Avançar
                                        <MdArrowForward />
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        disabled={!isFormValid || isLoading}
                                        className="btn btn-secondary gap-2"
                                    >
                                        {isLoading ? (
                                            <span className="loading loading-spinner"></span>
                                        ) : (
                                            <>
                                                Criar conta
                                                <MdArrowForward />
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>

                            {currentStep > 1 && (
                                <p className="text-center text-sm text-base-content/50">
                                    Já tem uma conta?{' '}
                                    <Link
                                        to="/login"
                                        className="font-semibold text-secondary hover:underline"
                                    >
                                        Entrar
                                    </Link>
                                </p>
                            )}
                        </form>
                    </div>
                </div>
            </div>

            <BackdropLoading visible={isLoading} />
        </div>
    )
}

/**
 * Campo do cadastro: rótulo acima, ícone DENTRO da caixa e texto de apoio
 * opcional. O anterior era um input sublinhado com o ícone em `left-0`, ou seja
 * fora da caixa e encostado no texto digitado.
 */
function RegisterField({
    icon,
    label,
    name,
    type,
    placeholder,
    value,
    onChange,
    required,
    hint,
    inputMode,
    autoComplete,
}: {
    icon: React.ReactNode
    label: string
    name: string
    type: string
    placeholder: string
    value: string
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    required?: boolean
    hint?: string
    inputMode?: 'text' | 'numeric' | 'tel' | 'email'
    autoComplete?: string
}) {
    return (
        <div className="flex flex-col gap-2">
            <label
                htmlFor={`register-${name}`}
                className="flex items-center gap-1.5 text-sm font-medium text-base-content"
            >
                {label}
                {required && <span className="text-error">*</span>}
            </label>
            <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-lg text-base-content/40">
                    {icon}
                </span>
                <input
                    id={`register-${name}`}
                    name={name}
                    type={type}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    inputMode={inputMode}
                    autoComplete={autoComplete}
                    className="input input-bordered w-full pl-10"
                    required={required}
                />
            </div>
            {hint && (
                <p className="text-xs leading-relaxed text-base-content/50">
                    {hint}
                </p>
            )}
        </div>
    )
}
